import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { trackEvent, EventType } from "@/shared/utils/core";
import { generateTransactionsPdf } from "@/shared/utils/generateTransactionsPdf";
import { useTransactions, useUpdateTransaction } from "@/shared/hooks/useTransactions";

import type { PlaidConnection } from "@/shared/types/estateTypes";

interface ConnectionsResponse {
  connections: PlaidConnection[];
}

async function fetchConnections(estateId: number): Promise<PlaidConnection[]> {
  const { data, error } = await supabase.functions.invoke(
    `financials/${estateId}/banking/connections?purpose=estate_account`,
    { method: "GET" },
  );
  if (error) throw new Error(error.message || "Failed to fetch connections");
  return (data as ConnectionsResponse).connections ?? [];
}

interface SendReminderParams {
  estateId: number;
  lawyerUserId: number;
}

interface SendReminderResult {
  alreadyPending: boolean;
  sentAt: string | null;
}

export function useLawyerEstateAccount(
  estateId: number | undefined,
  lawyerUserId: number | undefined,
) {
  const queryClient = useQueryClient();

  const connectionsQuery = useQuery({
    queryKey: ["lawyer-estate-account-connections", estateId],
    queryFn: () => fetchConnections(estateId!),
    enabled: !!estateId,
    staleTime: 5 * 60 * 1000,
  });

  const connections = connectionsQuery.data ?? [];
  const activeConnection = connections[0] ?? null;

  const {
    transactions,
    isLoading: isLoadingTransactions,
    error: transactionsError,
  } = useTransactions(estateId ?? null, activeConnection?.id ?? null);

  const { updateTransaction, isUpdating } = useUpdateTransaction(
    estateId ?? null,
    activeConnection?.id ?? null,
  );

  const reminderStatusQuery = useQuery({
    queryKey: ["lawyer-estate-account-reminder", estateId, lawyerUserId],
    queryFn: async () => {
      if (!estateId) return null;
      const { data, error } = await supabase.functions.invoke(
        `communications/estate-account-reminder/${estateId}`,
        { method: "GET" },
      );
      if (error) return null;
      return (data as { sent_at: string | null })?.sent_at ?? null;
    },
    enabled: !!estateId && !!lawyerUserId,
    staleTime: 60 * 1000,
  });

  const sendReminderMutation = useMutation({
    mutationFn: async (params: SendReminderParams): Promise<SendReminderResult> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated");

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${baseUrl}/functions/v1/communications/estate-account-reminder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          estate_id: params.estateId,
        }),
      });

      const body = (await res.json()) as Record<string, unknown>;

      if (res.status === 409) {
        return { alreadyPending: true, sentAt: (body.sent_at as string) ?? null };
      }

      if (!res.ok) {
        throw new Error((body.error as string) ?? "Failed to send reminder");
      }

      trackEvent(EventType.LAWYER_ESTATE_ACCOUNT_REMINDER_SENT, {
        estate_id: params.estateId,
        source: "lawyer_estate_account_tab",
      });

      return { alreadyPending: false, sentAt: (body.sent_at as string) ?? null };
    },
    onSuccess: (result, params) => {
      if (result.sentAt) {
        queryClient.setQueryData(
          ["lawyer-estate-account-reminder", params.estateId, params.lawyerUserId],
          result.sentAt,
        );
      }
    },
  });

  const [isExporting, setIsExporting] = useState(false);

  const exportPdf = useCallback(
    async (decedentFullName: string) => {
      if (!activeConnection) return;
      setIsExporting(true);
      try {
        let moneyIn = 0;
        let moneyOut = 0;
        for (const t of transactions) {
          if (t.is_inflow) moneyIn += t.amount;
          else moneyOut += t.amount;
        }
        await generateTransactionsPdf({
          transactions,
          accountName: activeConnection.institution_name ?? "Estate account",
          estateDisplayName: decedentFullName ? `Estate of ${decedentFullName}` : "Estate",
          balance: moneyIn - moneyOut,
          moneyIn,
          moneyOut,
        });
        trackEvent(EventType.LAWYER_ESTATE_ACCOUNT_PDF_EXPORTED, {
          estate_id: estateId,
          source: "lawyer_estate_account_tab",
        });
      } catch {
        throw new Error("Could not generate PDF");
      } finally {
        setIsExporting(false);
      }
    },
    [activeConnection, transactions, estateId],
  );

  return {
    connections,
    activeConnection,
    isLoadingConnections: connectionsQuery.isLoading,
    connectionsError: connectionsQuery.error,
    transactions,
    isLoadingTransactions,
    transactionsError,
    updateTransaction,
    isUpdating,
    reminderSentAt: reminderStatusQuery.data ?? null,
    isLoadingReminderStatus: reminderStatusQuery.isLoading,
    sendReminder: sendReminderMutation.mutateAsync,
    isSendingReminder: sendReminderMutation.isPending,
    exportPdf,
    isExporting,
  };
}
